import { useEffect, useRef, useState } from "react";
import { Plus, Search, Trash2, Copy as CopyIcon, Pencil, Eye, Files, Languages, Loader2, Braces } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSearchableList } from "@/hooks/useSearchableList";
import { PageHeader, Field, EmptyState, CopyButton, inputCls } from "@/components/shared";

const STATUSES = ["active", "draft", "archived"];

const emptyTemplate = {
  title_en: "", title_fr: "", body_en: "", body_fr: "",
  category: "", status: "active",
};

const statusStyles = {
  active: "bg-green-50 text-[#3F6212] border-green-100",
  draft: "bg-stone-100 text-stone-600 border-stone-200",
  archived: "bg-stone-800 text-white border-stone-800",
};

const CardAction = ({ testId, onClick, title, children }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    title={title}
    aria-label={title}
    className="p-2 rounded-lg text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
  >
    {children}
  </button>
);

const TemplateCard = ({ tpl, lang, t, onEdit, onDuplicate, onDelete, onCopy, onPreview }) => {
  const status = tpl.status || "active";
  const title = lang === "fr" && tpl.title_fr ? tpl.title_fr : tpl.title_en;
  const isSystem = !tpl.user_id;
  return (
    <div data-testid={`template-card-${tpl.id}`} className="bg-white border border-stone-200 rounded-xl p-5 hover:border-stone-300 hover:shadow-sm transition-all duration-200 flex flex-col">
      <div className="mb-3">
        <h3 className="font-heading text-lg font-medium text-stone-900">{title}</h3>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {tpl.category && <span className="text-[10px] uppercase tracking-wider text-stone-400">{tpl.category}</span>}
          <span data-testid={`template-status-${tpl.id}`} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusStyles[status] || statusStyles.active}`}>
            {t(`status_${status}`)}
          </span>
          {isSystem && (
            <span data-testid={`template-system-${tpl.id}`} className="inline-flex items-center rounded-full border border-[#9F8E7E]/30 bg-[#F5F1EA] text-[#7B6B5C] px-2 py-0.5 text-[10px] font-medium">
              {t("systemDefault")}
            </span>
          )}
          {tpl.updated_at && (
            <span className="text-[10px] text-stone-400">
              {t("updated")}: {fmtDate(tpl.updated_at, lang)}
            </span>
          )}
        </div>
      </div>
      <pre className="whitespace-pre-wrap font-body text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-lg p-3.5 max-h-28 overflow-hidden leading-relaxed flex-1">
        {tpl[`body_${lang}`] || tpl.body_en || "—"}
      </pre>
      <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between">
        <div className="flex items-center gap-0.5">
          <CardAction testId={`template-preview-${tpl.id}`} onClick={() => onPreview(tpl)} title={t("preview")}>
            <Eye size={15} strokeWidth={1.5} />
          </CardAction>
          <CardAction testId={`template-copy-${tpl.id}`} onClick={() => onCopy(tpl)} title={t("copy")}>
            <CopyIcon size={15} strokeWidth={1.5} />
          </CardAction>
          <CardAction testId={`template-duplicate-${tpl.id}`} onClick={() => onDuplicate(tpl)} title={t("duplicate")}>
            <Files size={15} strokeWidth={1.5} />
          </CardAction>
          {!isSystem && (
            <CardAction testId={`template-delete-${tpl.id}`} onClick={() => onDelete(tpl)} title={t("delete")}>
              <Trash2 size={15} strokeWidth={1.5} />
            </CardAction>
          )}
        </div>
        <button
          data-testid={`template-edit-${tpl.id}`}
          onClick={() => onEdit(tpl)}
          className="inline-flex items-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
        >
          <Pencil size={13} strokeWidth={1.5} /> {t("edit")}
        </button>
      </div>
    </div>
  );
};

const PreviewDialog = ({ tpl, onClose, onEdit, t, lang }) => {
  if (!tpl) return null;
  const title = lang === "fr" && tpl.title_fr ? tpl.title_fr : tpl.title_en;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-medium text-stone-900">{title}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap mt-1">
          {tpl.category && <span className="text-[11px] uppercase tracking-wider text-stone-500">{tpl.category}</span>}
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${statusStyles[tpl.status || "active"]}`}>
            {t(`status_${tpl.status || "active"}`)}
          </span>
          {tpl.updated_at && <span className="text-[11px] text-stone-400">{t("updated")}: {fmtDate(tpl.updated_at, lang)}</span>}
        </div>
        <Tabs defaultValue={lang} className="mt-4">
          <TabsList className="bg-stone-100 h-auto p-1">
            <TabsTrigger value="en" data-testid="preview-tab-en">EN</TabsTrigger>
            <TabsTrigger value="fr" data-testid="preview-tab-fr">FR</TabsTrigger>
          </TabsList>
          {["en", "fr"].map((l) => (
            <TabsContent key={l} value={l}>
              <pre data-testid={`preview-body-${l}`} className="whitespace-pre-wrap font-body text-sm text-stone-700 bg-stone-50 border border-stone-200 rounded-xl p-5 mt-3 leading-relaxed">
                {tpl[`body_${l}`] || "—"}
              </pre>
              <div className="mt-3">
                <CopyButton text={tpl[`body_${l}`]} testId={`preview-copy-${l}`} />
              </div>
            </TabsContent>
          ))}
        </Tabs>
        <div className="flex justify-end gap-3 mt-6">
          <button data-testid="preview-close-button" onClick={onClose} className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-5 py-2 text-sm font-medium transition-colors">
            {t("close")}
          </button>
          <button data-testid="preview-edit-button" onClick={() => onEdit(tpl)} className="inline-flex items-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2 text-sm font-medium transition-colors">
            <Pencil size={14} strokeWidth={1.5} /> {t("edit")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const insertAtCursor = (textareaRef, form, field, token, setForm) => {
  const ta = textareaRef.current;
  const current = form[field] || "";
  if (!ta) {
    setForm({ ...form, [field]: current + token });
    return;
  }
  const start = ta.selectionStart ?? current.length;
  const end = ta.selectionEnd ?? current.length;
  const next = current.slice(0, start) + token + current.slice(end);
  setForm({ ...form, [field]: next });
  // Restore focus + move cursor after inserted token
  requestAnimationFrame(() => {
    ta.focus();
    ta.setSelectionRange(start + token.length, start + token.length);
  });
};

const PlaceholderPicker = ({ placeholders, onPick, testId, label }) => {
  if (!placeholders?.length) return null;
  const grouped = placeholders.reduce((acc, p) => {
    (acc[p.source] = acc[p.source] || []).push(p);
    return acc;
  }, {});
  return (
    <select
      data-testid={testId}
      value=""
      onChange={(e) => {
        if (e.target.value) {
          onPick(e.target.value);
          e.target.value = "";
        }
      }}
      className="text-[11px] bg-white border border-stone-200 rounded-md px-2 py-1 text-stone-600 hover:border-stone-400 focus:outline-none focus:ring-1 focus:ring-slate-900"
    >
      <option value="">{label}</option>
      {Object.entries(grouped).map(([source, list]) => (
        <optgroup key={source} label={source}>
          {list.map((p) => (
            <option key={p.token} value={p.token}>{p.token} — {p.label}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
};

const EditFullscreenDialog = ({ form, setForm, saving, autoTranslate, setAutoTranslate, translating, onTranslateNow, dirtyLang, onSave, onCancel, onDelete, onDuplicate, placeholders, t, lang }) => {
  const enRef = useRef(null);
  const frRef = useRef(null);
  if (!form) return null;
  const trackedSet = (k, source) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm({ ...form, [k]: val, __sourceLang: source });
  };
  const otherLang = dirtyLang === "en" ? "fr" : "en";
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent
        data-testid="template-edit-fullscreen"
        className="w-screen h-screen max-w-none max-h-none rounded-none p-0 flex flex-col bg-white sm:rounded-none"
      >
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 border-b border-stone-200 px-6 py-4 flex-wrap">
          <DialogHeader className="flex-1 min-w-0">
            <DialogTitle className="font-heading text-xl sm:text-2xl font-medium text-stone-900 truncate">
              {form.id ? t("editTemplate") : t("addTemplate")}
            </DialogTitle>
            {form.updated_at && (
              <p className="text-xs text-stone-400 mt-0.5">
                {t("updated")}: {fmtDate(form.updated_at, lang)}
              </p>
            )}
          </DialogHeader>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 text-xs text-stone-600 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="auto-translate-toggle"
                checked={autoTranslate}
                onChange={(e) => setAutoTranslate(e.target.checked)}
                className="rounded border-stone-300 text-slate-900 focus:ring-slate-900"
              />
              <Languages size={14} strokeWidth={1.5} />
              {t("autoTranslateLabel")}
            </label>
            <button
              data-testid="edit-cancel-button"
              onClick={onCancel}
              className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-5 py-2 text-sm font-medium transition-colors">
              {t("cancel")}
            </button>
            <button
              data-testid="edit-save-button"
              onClick={onSave}
              disabled={saving || translating}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2 text-sm font-medium transition-colors disabled:opacity-60 inline-flex items-center gap-2">
              {(saving || translating) && <Loader2 size={14} className="animate-spin" />}
              {translating ? t("translating") : (saving ? "…" : t("save"))}
            </button>
          </div>
        </div>

        {autoTranslate && dirtyLang && (
          <div data-testid="translate-source-banner" className="px-6 py-2 bg-[#F5F1EA] border-b border-stone-200 text-xs text-stone-600 flex items-center gap-2">
            <Languages size={13} strokeWidth={1.5} className="text-[#9F8E7E]" />
            {t("sourceLangHint").replace("{src}", t(`lang_${dirtyLang}`)).replace("{tgt}", t(`lang_${otherLang}`))}
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={`${t("titleEn")} *`}>
              <input data-testid="template-title-en-input" className={inputCls} value={form.title_en} onChange={trackedSet("title_en", "en")} />
            </Field>
            <Field label={t("titleFr")}>
              <input data-testid="template-title-fr-input" className={inputCls} value={form.title_fr} onChange={trackedSet("title_fr", "fr")} />
            </Field>
            <Field label={t("category")}>
              <input data-testid="template-category-input" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </Field>
            <Field label={t("status")}>
              <Select value={form.status || "active"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="template-status-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`template-status-option-${s}`}>
                      {t(`status_${s}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500">{t("bodyEn")}</label>
                <div className="flex items-center gap-2">
                  <PlaceholderPicker
                    placeholders={placeholders}
                    testId="placeholder-picker-en"
                    label={t("insertPlaceholder")}
                    onPick={(token) => insertAtCursor(enRef, form, "body_en", token, (next) => setForm({ ...next, __sourceLang: "en" }))}
                  />
                  <button
                    data-testid="translate-to-en-button"
                    type="button"
                    onClick={() => onTranslateNow("fr")}
                    disabled={translating || !form.body_fr?.trim()}
                    className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    <Languages size={12} strokeWidth={1.5} /> {t("translateFromFr")}
                  </button>
                </div>
              </div>
              <textarea ref={enRef} data-testid="template-body-en-input" rows={12} className={inputCls} value={form.body_en} onChange={trackedSet("body_en", "en")} />
            </div>
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500">{t("bodyFr")}</label>
                <div className="flex items-center gap-2">
                  <PlaceholderPicker
                    placeholders={placeholders}
                    testId="placeholder-picker-fr"
                    label={t("insertPlaceholder")}
                    onPick={(token) => insertAtCursor(frRef, form, "body_fr", token, (next) => setForm({ ...next, __sourceLang: "fr" }))}
                  />
                  <button
                    data-testid="translate-to-fr-button"
                    type="button"
                    onClick={() => onTranslateNow("en")}
                    disabled={translating || !form.body_en?.trim()}
                    className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    <Languages size={12} strokeWidth={1.5} /> {t("translateFromEn")}
                  </button>
                </div>
              </div>
              <textarea ref={frRef} data-testid="template-body-fr-input" rows={12} className={inputCls} value={form.body_fr} onChange={trackedSet("body_fr", "fr")} />
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        {form.id && (
          <div className="border-t border-stone-200 px-6 py-3 flex items-center gap-4 flex-wrap">
            {form.user_id && (
              <button
                data-testid="edit-delete-button"
                onClick={onDelete}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[#7F1D1D] hover:underline">
                <Trash2 size={14} strokeWidth={1.5} /> {t("delete")}
              </button>
            )}
            {!form.user_id && (
              <span className="text-xs text-stone-400 inline-flex items-center gap-1.5">
                <Braces size={13} strokeWidth={1.5} /> {t("systemTemplateHint")}
              </span>
            )}
            <button
              data-testid="edit-duplicate-button"
              onClick={() => onDuplicate(form)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 hover:underline">
              <Files size={14} strokeWidth={1.5} /> {t("duplicate")}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Templates() {
  const { items, search, setSearch, reload } = useSearchableList("/templates");
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [autoTranslate, setAutoTranslate] = useState(true);
  const [placeholders, setPlaceholders] = useState([]);
  const { t, lang } = useLang();

  useEffect(() => {
    api.get("/placeholders").then((r) => setPlaceholders(r.data)).catch(() => setPlaceholders([]));
  }, []);

  const dirtyLang = form?.__sourceLang || null;

  const validate = () => {
    if (!form.title_en?.trim()) return t("templateValidationTitle");
    if (!form.body_en?.trim() && !form.body_fr?.trim()) return t("templateValidationBody");
    return null;
  };

  const callTranslate = async (source_lang, title, body) => {
    const { data } = await api.post("/ai/translate-template", { source_lang, title, body });
    return data; // { target_lang, title, body }
  };

  const translateNow = async (source_lang) => {
    const title = source_lang === "en" ? form.title_en : form.title_fr;
    const body = source_lang === "en" ? form.body_en : form.body_fr;
    if (!body?.trim()) return;
    setTranslating(true);
    try {
      const out = await callTranslate(source_lang, title || "", body);
      const patch = out.target_lang === "fr"
        ? { title_fr: out.title || form.title_fr, body_fr: out.body }
        : { title_en: out.title || form.title_en, body_en: out.body };
      setForm({ ...form, ...patch, __sourceLang: source_lang });
      toast.success(t("translatedNotice").replace("{tgt}", t(`lang_${out.target_lang}`)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("translateFailed"));
    } finally {
      setTranslating(false);
    }
  };

  const save = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      let payload = { ...form };
      delete payload.__sourceLang;

      if (autoTranslate && dirtyLang) {
        const srcTitle = dirtyLang === "en" ? payload.title_en : payload.title_fr;
        const srcBody = dirtyLang === "en" ? payload.body_en : payload.body_fr;
        if ((srcBody || "").trim()) {
          setTranslating(true);
          try {
            const out = await callTranslate(dirtyLang, srcTitle || "", srcBody);
            if (out.target_lang === "fr") {
              payload.title_fr = out.title || payload.title_fr;
              payload.body_fr = out.body || payload.body_fr;
            } else {
              payload.title_en = out.title || payload.title_en;
              payload.body_en = out.body || payload.body_en;
            }
          } catch (e) {
            toast.error(t("translateFailed"));
          } finally {
            setTranslating(false);
          }
        }
      }

      let saved;
      if (payload.id) {
        const { data } = await api.put(`/templates/${payload.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/templates", payload);
        saved = data;
      }
      toast.success(t("saved"));
      // Keep the dialog open so Julia can review both language tabs
      setForm({ ...emptyTemplate, ...saved });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async (tpl) => {
    const copy = {
      title_en: `${tpl.title_en} (copy)`,
      title_fr: tpl.title_fr ? `${tpl.title_fr} (copie)` : "",
      body_en: tpl.body_en || "",
      body_fr: tpl.body_fr || "",
      category: tpl.category || "",
      status: "draft",
    };
    try {
      const { data } = await api.post("/templates", copy);
      toast.success(t("duplicated"));
      reload();
      setForm({ ...emptyTemplate, ...data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeInline = async (tpl) => {
    const name = lang === "fr" && tpl.title_fr ? tpl.title_fr : tpl.title_en;
    if (!window.confirm(t("confirmDeleteTemplate").replace("{name}", name))) return;
    await api.delete(`/templates/${tpl.id}`);
    toast.success(t("deleted"));
    reload();
  };

  const removeFromForm = async () => {
    await removeInline(form);
    setForm(null);
  };

  const copyBody = async (tpl) => {
    const text = tpl[`body_${lang}`] || tpl.body_en || "";
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("copied"));
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(t("copied"));
    }
  };

  const openEditFromPreview = (tpl) => {
    setPreview(null);
    setForm({ ...emptyTemplate, ...tpl });
  };

  return (
    <div data-testid="templates-page">
      <PageHeader
        title={t("templates")}
        subtitle={t("templatesSubtitle")}
        action={
          <button data-testid="add-template-button" onClick={() => setForm({ ...emptyTemplate })}
            className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors">
            <Plus size={16} strokeWidth={1.5} /> {t("addTemplate")}
          </button>
        }
      />
      <div className="relative mb-6 max-w-md">
        <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input data-testid="templates-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t("search")} className={`${inputCls} pl-9`} />
      </div>

      {items.length === 0 ? <EmptyState message={t("noResults")} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((tpl) => (
            <TemplateCard
              key={tpl.id}
              tpl={tpl}
              lang={lang}
              t={t}
              onEdit={(x) => setForm({ ...emptyTemplate, ...x })}
              onDuplicate={duplicate}
              onDelete={removeInline}
              onCopy={copyBody}
              onPreview={setPreview}
            />
          ))}
        </div>
      )}

      <PreviewDialog
        tpl={preview}
        onClose={() => setPreview(null)}
        onEdit={openEditFromPreview}
        t={t}
        lang={lang}
      />

      <EditFullscreenDialog
        form={form}
        setForm={setForm}
        saving={saving}
        autoTranslate={autoTranslate}
        setAutoTranslate={setAutoTranslate}
        translating={translating}
        onTranslateNow={translateNow}
        dirtyLang={dirtyLang}
        onSave={save}
        onCancel={() => setForm(null)}
        onDelete={removeFromForm}
        onDuplicate={duplicate}
        placeholders={placeholders}
        t={t}
        lang={lang}
      />
    </div>
  );
}
